import { useCallback } from 'react';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { performPostRequest } from '@atlassian/jira-fetch';
import { useFlagsService, toFlagId } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { IssueKey } from '@atlassian/jira-shared-types';
import { messages } from './messages';

const getRestApiPath = (issueKey: IssueKey) =>
	`/rest/internal/latest/issue/${issueKey}/assignInvitedUser`;

type AssignInvitedUserRequest = {
	issueKey: IssueKey;
	accountId: string;
};

export const ERROR_FLAG_ID = toFlagId('ASSIGN_INVITED_USER_ERROR');

export const useAssignInvitedUserService = () => {
	const { showFlag } = useFlagsService();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		async ({ issueKey, accountId }: AssignInvitedUserRequest) => {
			try {
				await performPostRequest(getRestApiPath(issueKey), {
					body: JSON.stringify({
						invitedUserId: accountId,
					}),
				});
				const analyticsEvent = createAnalyticsEvent({});
				const attributes = {
					fieldKey: 'assignee',
					fieldType: 'user',
					isAssignInvitedUser: true,
				};
				// intentionally firing the event even if BE call fails to update the field
				fireTrackAnalytics(analyticsEvent, 'field updated', attributes);
				fireOperationalAnalytics(analyticsEvent, 'field updateSucceeded', attributes);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useAssignInvitedUserService',
						packageName: 'jiraIssueViewServices',
						// eslint-disable-next-line jira/js/team-name-enforcer-for-error-analytics
						teamName: 'jlove-makkuro',
					},
					error,
				});

				showFlag({
					id: ERROR_FLAG_ID,
					type: 'error',
					title: formatMessage(messages.errorFlagTitle),
					description: formatMessage(messages.errorFlagDescription),
					isAutoDismiss: true,
				});
			}
		},
		[createAnalyticsEvent, formatMessage, showFlag],
	);
};
